import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertTriangle, Compass, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/dates";

const HEALTH_CHIP = { green: "patria-chip-green", yellow: "patria-chip-terra", red: "patria-chip-terra" };

export default function RhythmDashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [metadataOnly, setMetadataOnly] = useState(false);
  const [viewScope, setViewScope] = useState("oversight");
  const [flagsVisible, setFlagsVisible] = useState(true);
  const [campuses, setCampuses] = useState([]);
  const [filterCampus, setFilterCampus] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const role = user?.role;
  const isPO = ["po_admin", "super_admin"].includes(role);
  const isPastor = !!user?.is_campus_pastor;
  // Round 3 Section A2: supervisors (non-PO, non-pastor) land on /team.
  // Employees and volunteers land on /rhythm.
  const shouldRedirect = role === "employee" || role === "volunteer";
  const shouldRedirectSupervisor = !shouldRedirect && !isPO && !isPastor;

  const load = async () => {
    try {
      const { data } = await api.get(`/rhythm/dashboard${filterCampus ? `?campus_id=${filterCampus}` : ""}`);
      setRows(data.rows);
      setSummary(data.summary);
      setMetadataOnly(!!data.campus_pastor_metadata_only);
      setViewScope(data.view_scope || "oversight");
      setFlagsVisible(data.flags_visible !== false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error("Couldn't load the RHYTHM dashboard.");
      }
    }
  };

  useEffect(() => { if (!shouldRedirect) load(); /* eslint-disable-next-line */ }, [filterCampus, shouldRedirect]);
  useEffect(() => {
    if (shouldRedirect) return;
    api.get("/campuses").then((r) => setCampuses(r.data)).catch(() => {});
  }, [shouldRedirect]);

  if (shouldRedirect || forbidden) {
    return <Navigate to="/rhythm" replace />;
  }
  if (shouldRedirectSupervisor) {
    // Supervisors: absorb 4 screens into /team.
    return <Navigate to="/team" replace />;
  }

  const isTeamView = viewScope === "team";
  const isPastorView = viewScope === "campus_pastor";
  const dashboardTitle = isTeamView ? "Team Dashboard" : "RHYTHM Dashboard";
  const dashboardSubtitle = isTeamView
    ? "Your direct reports — cadence health, action items, and goal progress at a glance."
    : "Where the whole team's 1:1 rhythm lives. R/Y/G at a glance, action items, goals, and COMPASS flags — all in one warm oversight view.";

  return (
    <div className="space-y-6" data-testid="rhythm-dashboard-page">
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 hover:text-forest-900">← Back To RHYTHM</Link>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium mt-2">
          {isTeamView ? "Your Team" : "Oversight"}
        </p>
        <h1 className="text-4xl font-serif text-forest-900" data-testid="rhythm-dashboard-title">
          {dashboardTitle}
        </h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">{dashboardSubtitle}</p>
        {metadataOnly && (
          <div className="mt-3 p-3 rounded-xl bg-terracotta-500/10 border-l-4 border-terracotta-500 text-sm text-charcoal-soft" data-testid="pastor-metadata-notice">
            <ShieldAlert className="inline h-4 w-4 mr-1 text-terracotta-600" strokeWidth={1.5} />
            Campus Pastor view — you see metadata (dates, health, counts) but not the content of any 1:1 record unless you are the supervisor of that pairing.
          </div>
        )}
      </div>

      {summary && (
        <section className="grid grid-cols-4 gap-3" data-testid="rhythm-dash-summary">
          <SummaryCard label="Pairings" value={summary.total} />
          <SummaryCard label="On Cadence" value={summary.green} tone="green" />
          <SummaryCard label="Slipping" value={summary.yellow} tone="yellow" />
          <SummaryCard label="Cadence Red" value={summary.red} tone="red" />
        </section>
      )}

      {/* Team + Campus Pastor views have no campus filter — supervisors/pastors are locked to their scope. */}
      {!isTeamView && !isPastorView && (
        <section className="patria-card p-4 flex items-center gap-3 flex-wrap" data-testid="rhythm-dashboard-filters">
          <select
            className="patria-input text-sm"
            value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}
            data-testid="rhythm-filter-campus"
          >
            <option value="">All Campuses</option>
            {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {isPO && (
            <Link to="/rhythm/admin" className="patria-btn-secondary text-sm ml-auto">Open Admin Settings</Link>
          )}
        </section>
      )}

      <section className="patria-card overflow-x-auto">
        <table className="patria-table w-full text-sm">
          <thead>
            <tr>
              <th>Teammate</th>
              <th>Supervisor</th>
              <th>Campus</th>
              <th>Last 1:1</th>
              <th>Cadence</th>
              <th>Open · Overdue</th>
              <th>Goals</th>
              {flagsVisible && <th data-testid="dash-col-flags">Flags</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={flagsVisible ? 8 : 7} className="italic text-charcoal-muted p-4">No rows to show.</td></tr>}
            {rows.map((r) => (
              <tr key={r.pairing_id} data-testid={`rhythm-dash-row-${r.pairing_id}`}>
                <td>
                  {metadataOnly
                    ? <span>{r.employee_name}</span>
                    : <Link to={`/rhythm/workspace/${r.pairing_id}`} className="text-forest-700 hover:text-forest-900 underline">{r.employee_name}</Link>}
                </td>
                <td>{r.supervisor_name}</td>
                <td className="text-xs text-charcoal-muted">{r.employee_campus || "—"}</td>
                <td className="text-xs">{r.last_1on1 ? formatDate(r.last_1on1) : "—"}</td>
                <td>
                  <span className={HEALTH_CHIP[r.health]}>
                    {r.health !== "green" && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {r.health.toUpperCase()}
                    {r.missed_cycles > 0 && ` · ${r.missed_cycles}`}
                  </span>
                </td>
                <td>{r.open_actions} · <span className={r.overdue_actions > 0 ? "text-terracotta-600 font-medium" : ""}>{r.overdue_actions}</span></td>
                <td className="text-xs">
                  {Object.entries(r.goal_counts).filter(([, v]) => v > 0).map(([k, v]) => (
                    <span key={k} className="mr-2">{v} {k.replaceAll("_", " ")}</span>
                  ))}
                </td>
                {flagsVisible && (
                  <td>
                    {r.compass_active && <span className="patria-chip-terra"><Compass className="h-3 w-3 mr-1" /> COMPASS</span>}
                    {r.lift_cycle_status && <span className="patria-chip-green ml-1">LIFT {r.lift_cycle_status}</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, tone = "neutral" }) {
  const toneCls = {
    green: "bg-forest-900/10 text-forest-900",
    yellow: "bg-terracotta-500/15 text-terracotta-700",
    red: "bg-terracotta-500/30 text-terracotta-700",
    neutral: "bg-cream-100 text-forest-900",
  }[tone];
  return (
    <div className={`patria-card p-4 ${toneCls}`}>
      <p className="text-[10px] uppercase tracking-widest">{label}</p>
      <p className="mt-1 text-3xl font-serif">{value}</p>
    </div>
  );
}
